import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { OnboardingShell } from "@/components/shells/OnboardingShell";
import { toast } from "sonner";

const SECTIONS = [
  {
    title: "1 · How the buying group works",
    body: "Gift Card Buyers Club is a members-only buying group. We pool the unused gift-card supply of vetted members to negotiate rates a single seller can't, and pass the negotiated rate back to members net of operating margin. Membership is at-will and free of charge.",
  },
  {
    title: "2 · Source-of-cards eligibility",
    body: "You confirm your gift cards come from a legitimate, verifiable source as declared in your application. Reseller resale, anonymous-trade volume, and bulk peer-to-peer trade origins are not eligible. Misrepresentation is grounds for immediate suspension and forfeiture of pending payouts.",
  },
  {
    title: "3 · Reservations & submissions",
    body: "Reserving a deal locks your share of available capacity at the published rate. Cards must be submitted before the deal deadline. Unfilled reservations are released back to the pool when a deal closes — there is no penalty for unfilled portions.",
  },
  {
    title: "4 · Payouts",
    body: "Payouts are issued by ACH to the bank account on file according to each deal's published payment terms. We file IRS Form 1099-NEC for any member earning $600 or more in a calendar year, sent by January 31 of the following year.",
  },
  {
    title: "5 · Privacy & data handling",
    body: "Card numbers, PINs, and bank account numbers are encrypted at rest with AWS KMS. Decryption is restricted to Payments Overseers and Super Admins, and every decryption event is recorded in an immutable audit log with the actor's ID, IP, and timestamp. Bank account data is purged 90 days after final payout if your account is closed.",
  },
  {
    title: "6 · Account closure",
    body: "You may deactivate your account at any time. Pending payouts complete on the original payment terms. Identity records are retained for 7 years to comply with US financial recordkeeping requirements.",
  },
];

export default function OnboardingTermsPage() {
  const navigate = useNavigate();
  const scrollerRef = useRef(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledToBottom(true);
  };

  const finish = () => {
    toast.success("Terms accepted · You're an active member", {
      description: "Welcome to GCBC. Your dashboard is ready.",
    });
    setTimeout(() => navigate("/member/dashboard"), 800);
  };

  return (
    <OnboardingShell currentStage="terms">
      <div className="eyebrow mb-3 text-brand-700">Step 5 of 6</div>
      <h1 className="font-brand text-[40px] lg:text-[44px] leading-[1.05] tracking-[-0.025em] text-ink font-bold">
        Final step: accept the terms.
      </h1>
      <p className="mt-5 text-body text-ink-muted leading-relaxed max-w-xl">
        Plain-English summary below. Scroll to the bottom to enable acceptance.
      </p>

      <div className="mt-9 max-w-xl border-y border-line">
        <div ref={scrollerRef} onScroll={onScroll}
             className="max-h-[440px] overflow-auto py-7 space-y-7 text-body-sm text-ink-muted leading-relaxed pr-2">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h3 className="text-h3 text-ink font-semibold mb-2">{s.title}</h3>
              <p>{s.body}</p>
            </section>
          ))}
          <div className="text-caption text-ink-subtle pt-4 border-t border-line/60">
            Full legal Terms of Service and Privacy Policy are available at <a href="/faq" className="text-brand-700 underline">gcbc.com/legal</a>. By accepting below, you confirm you've read and agree to both documents.
          </div>
        </div>
      </div>

      <div className="mt-7 max-w-xl">
        <label className="flex items-start gap-3 text-body-sm cursor-pointer">
          <Checkbox
            checked={accepted}
            onCheckedChange={(v) => setAccepted(!!v)}
            disabled={!scrolledToBottom}
            className="mt-0.5"
            data-testid="terms-checkbox"
          />
          <span className={scrolledToBottom ? "text-ink" : "text-ink-hint"}>
            I&rsquo;ve read and agree to the Terms of Service and Privacy Policy.
          </span>
        </label>
        {!scrolledToBottom && (
          <p className="mt-2 ml-7 text-caption text-ink-hint">Scroll to the bottom of the document to enable.</p>
        )}
      </div>

      <div className="mt-8 max-w-xl flex justify-end">
        <Button onClick={finish} disabled={!accepted} data-testid="accept-terms-btn"
                className="h-12 px-7 bg-brand-700 hover:bg-brand-800 text-white text-body-sm font-semibold group">
          <Check className="size-4 mr-1.5" /> Accept &amp; activate my account
          <ArrowRight className="size-4 ml-2 transition-transform group-hover:translate-x-0.5" />
        </Button>
      </div>
    </OnboardingShell>
  );
}
